import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { getTempo } from "../utils/tempo";

// Pop-out cast — ONLY the owner's provided pictures (blue iPhone, S22 Ultra
// + S Pen, Nothing pair, flip concept, cracked iPhone 17 Pro Max duo).
const POP_PHONES = [
  { src: "/phones/trans-iphone-blue.png", glow: "rgba(107,155,255,0.6)" },
  { src: "/phones/trans-s22u.png", glow: "rgba(165,87,116,0.6)" },
  { src: "/phones/ip17-cracked-back.png", glow: "rgba(240,146,62,0.55)" },
  { src: "/phones/trans-nothing.png", glow: "rgba(79,179,191,0.55)" },
  { src: "/phones/trans-iphone-flip.png", glow: "rgba(124,131,219,0.55)" },
  { src: "/phones/ip17-cracked-screen.png", glow: "rgba(122,111,208,0.5)" },
];

let turn = 0;

const FlipLeaf = ({ onDone }) => {
  const idx = turn % POP_PHONES.length;
  turn += 1;
  const phones = [POP_PHONES[idx], POP_PHONES[(idx + 2) % POP_PHONES.length]];
  const T = getTempo(); // destination page sets the pace

  useEffect(() => {
    const t = setTimeout(onDone, 1360 * T);
    return () => clearTimeout(t);
  }, [onDone, T]);

  return (
    <motion.div
      data-testid="page-flip-transition"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[70] overflow-hidden"
      style={{ perspective: "1900px" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.16 } }}
      exit={{ opacity: 0, transition: { duration: 0.37, ease: "easeInOut" } }}
    >
      {/* dim veil while the page turns */}
      <motion.div
        className="absolute inset-0 bg-espresso/70"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
      />

      {/* phones popping out from behind the turning leaf */}
      {phones.map((p, i) => (
        <motion.img
          key={p.src}
          src={p.src}
          alt=""
          draggable="false"
          className="absolute top-1/2 h-[26vh] w-auto sm:h-[36vh]"
          style={{ left: i ? "60%" : "32%", filter: `drop-shadow(0 0 46px ${p.glow})` }}
          initial={{ y: "16vh", x: "-50%", scale: 0.3, opacity: 0, rotate: i ? 16 : -16 }}
          animate={{ y: "-50%", scale: 1, opacity: 1, rotate: i ? 6 : -6 }}
          exit={{ y: "-36vh", opacity: 0, scale: 0.85, transition: { duration: 0.39 * T, ease: "easeIn" } }}
          transition={{ type: "spring", stiffness: 145 / T, damping: 18, delay: (0.28 + i * 0.12) * T }}
        />
      ))}

      {/* the turning leaf */}
      <motion.div
        className="absolute inset-0"
        style={{ transformOrigin: "left center", transformStyle: "preserve-3d" }}
        initial={{ rotateY: 0 }}
        animate={{ rotateY: -166 }}
        transition={{ duration: 1.17 * T, ease: [0.5, 0, 0.2, 1], delay: 0.07 * T }}
      >
        {/* front face — glossy dark page (kept layer-light for smoothness) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            background: "linear-gradient(125deg, #0B111B 30%, #121A26 62%, #182435 100%)",
          }}
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(115deg, rgba(244,244,239,0.10) 0%, rgba(244,244,239,0.02) 28%, transparent 45%, rgba(61,111,224,0.08) 78%, rgba(233,180,76,0.10) 100%)",
            }}
          />
          {/* travelling gloss sheen */}
          <motion.div
            className="absolute inset-y-[-20%] w-[38%]"
            style={{
              background:
                "linear-gradient(100deg, transparent, rgba(255,255,255,0.28) 45%, rgba(255,255,255,0.42) 50%, rgba(255,255,255,0.28) 55%, transparent)",
              transform: "skewX(-14deg)",
              mixBlendMode: "screen",
            }}
            initial={{ left: "-45%" }}
            animate={{ left: "125%" }}
            transition={{ duration: 1.09 * T, ease: [0.3, 0, 0.2, 1], delay: 0.12 * T }}
          />
          {/* glowing free edge of the page */}
          <div className="absolute inset-y-0 right-0 w-[3px] bg-gradient-to-b from-transparent via-volt/80 to-transparent" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-black/45 to-transparent" />
          <span className="absolute bottom-6 left-8 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40">
            Next-G · turning page
          </span>
        </div>
        {/* back face — glossy reverse with volt rim */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            background:
              "linear-gradient(250deg, rgba(11,17,27,1) 40%, rgba(30,34,48,1) 75%, rgba(52,64,92,1) 100%)",
          }}
        >
          <div className="absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-transparent via-volt/70 to-transparent" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(65deg, rgba(255,255,255,0.10), transparent 40%)" }}
          />
        </div>
      </motion.div>

      {/* hairline gloss progress along the top */}
      <motion.span
        className="absolute inset-x-0 top-0 h-[2px] origin-left bg-gradient-to-r from-volt via-accent to-magenta"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.21, ease: [0.3, 0, 0.2, 1] }}
      />
    </motion.div>
  );
};

export const PageFlipTransition = () => {
  const location = useLocation();
  const reduce = useReducedMotion();
  const [show, setShow] = useState(false);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (reduce) return;
    setShow(true);
  }, [location.pathname, reduce]);

  return (
    <AnimatePresence>
      {show && <FlipLeaf key={location.pathname} onDone={() => setShow(false)} />}
    </AnimatePresence>
  );
};
